import React, { useEffect, useRef } from 'react';
import { Box, IconButton, Tooltip, Typography, TextField, InputAdornment } from '@mui/material';
import { Add, GridView, ViewList, FilterList, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { PlaylistSortMode, PlaylistViewMode } from '../../services/sonosApi';
import { SONOS_BLUE, SURFACE_BORDER } from './sonosUiStyles';

const SORT_OPTIONS: PlaylistSortMode[] = ['manual', 'alpha', 'added_new', 'added_old'];

interface PlaylistLibraryToolbarProps {
  sortMode: PlaylistSortMode;
  onSortModeChange: (mode: PlaylistSortMode) => void;
  viewMode: PlaylistViewMode;
  onViewModeChange: (mode: PlaylistViewMode) => void;
  onAddPlaylist: () => void;
  filterQuery?: string;
  onFilterQueryChange?: (query: string) => void;
  filterOpen?: boolean;
  onFilterOpenChange?: (open: boolean) => void;
}

const sortLabelKey: Record<PlaylistSortMode, string> = {
  alpha: 'musicSystem.playlists.sortAlpha',
  alpha_desc: 'musicSystem.playlists.sortAlphaDesc',
  manual: 'musicSystem.playlists.sortManual',
  added_new: 'musicSystem.playlists.sortAddedNew',
  added_old: 'musicSystem.playlists.sortAddedOld',
};

const PlaylistLibraryToolbar: React.FC<PlaylistLibraryToolbarProps> = ({
  sortMode,
  onSortModeChange,
  viewMode,
  onViewModeChange,
  onAddPlaylist,
  filterQuery = '',
  onFilterQueryChange,
  filterOpen = false,
  onFilterOpenChange,
}) => {
  const { t } = useTranslation();
  const filterInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (filterOpen) {
      const id = window.requestAnimationFrame(() => filterInputRef.current?.focus());
      return () => window.cancelAnimationFrame(id);
    }
    return undefined;
  }, [filterOpen]);

  const handleSortClick = (mode: PlaylistSortMode) => {
    if (mode === 'alpha') {
      if (sortMode === 'alpha') onSortModeChange('alpha_desc');
      else if (sortMode === 'alpha_desc') onSortModeChange('alpha');
      else onSortModeChange('alpha');
      return;
    }
    onSortModeChange(mode);
  };

  const isAlphaActive = sortMode === 'alpha' || sortMode === 'alpha_desc';
  const alphaLabelKey = sortMode === 'alpha_desc'
    ? sortLabelKey.alpha_desc
    : sortLabelKey.alpha;

  const handleClearFilter = () => {
    onFilterQueryChange?.('');
    filterInputRef.current?.focus();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        mb: 1,
        width: '100%',
        minWidth: 0,
        flexShrink: 0,
      }}
    >
      <Box
        data-audio-no-swipe=""
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.4,
          flex: 1,
          minWidth: 0,
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          py: 0.25,
        }}
      >
        {SORT_OPTIONS.map((mode) => {
          if (mode === 'alpha') {
            return (
              <Box
                key={mode}
                onClick={() => handleSortClick('alpha')}
                sx={{
                  flexShrink: 0,
                  px: 1,
                  py: 0.35,
                  borderRadius: 999,
                  cursor: 'pointer',
                  border: `1px solid ${isAlphaActive ? SONOS_BLUE : SURFACE_BORDER}`,
                  backgroundColor: isAlphaActive ? 'rgba(35, 145, 255, 0.15)' : 'transparent',
                  userSelect: 'none',
                }}
              >
                <Typography sx={{
                  fontSize: 10,
                  fontWeight: isAlphaActive ? 700 : 400,
                  color: isAlphaActive ? SONOS_BLUE : '#fff',
                  whiteSpace: 'nowrap',
                }}>
                  {t(alphaLabelKey)}
                </Typography>
              </Box>
            );
          }

          const active = sortMode === mode;
          return (
            <Box
              key={mode}
              onClick={() => handleSortClick(mode)}
              sx={{
                flexShrink: 0,
                px: 1,
                py: 0.35,
                borderRadius: 999,
                cursor: 'pointer',
                border: `1px solid ${active ? SONOS_BLUE : SURFACE_BORDER}`,
                backgroundColor: active ? 'rgba(35, 145, 255, 0.15)' : 'transparent',
                userSelect: 'none',
              }}
            >
              <Typography sx={{
                fontSize: 10,
                fontWeight: active ? 700 : 400,
                color: active ? SONOS_BLUE : '#fff',
                whiteSpace: 'nowrap',
              }}>
                {t(sortLabelKey[mode])}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Tooltip title={t('musicSystem.playlists.filter')}>
        <IconButton
          size="small"
          onClick={() => onFilterOpenChange?.(!filterOpen)}
          sx={{
            color: filterOpen || filterQuery.trim() ? SONOS_BLUE : '#fff',
            p: 0.5,
            flexShrink: 0,
          }}
        >
          <FilterList sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      {filterOpen && (
        <TextField
          size="small"
          value={filterQuery}
          onChange={(e) => onFilterQueryChange?.(e.target.value)}
          placeholder={t('musicSystem.playlists.filterPlaceholder')}
          inputRef={filterInputRef}
          InputProps={{
            endAdornment: filterQuery ? (
              <InputAdornment position="end" sx={{ mr: 0.35 }}>
                <Tooltip title={t('musicSystem.playlists.clearFilter')}>
                  <IconButton
                    size="small"
                    onClick={handleClearFilter}
                    aria-label={t('musicSystem.playlists.clearFilter')}
                    sx={{
                      p: 0,
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      color: '#fff',
                      bgcolor: 'rgba(255,255,255,0.12)',
                      '&:hover': { bgcolor: '#555', color: '#fff' },
                    }}
                  >
                    <Close sx={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            flexShrink: 1,
            minWidth: 72,
            maxWidth: 148,
            '& .MuiInputBase-root': {
              color: '#fff',
              fontSize: 12,
              height: 28,
              py: 0,
              pr: filterQuery ? 0.75 : undefined,
            },
            '& .MuiInputAdornment-root': {
              ml: 0,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: SURFACE_BORDER,
            },
            '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: SONOS_BLUE,
            },
            '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: SONOS_BLUE,
            },
          }}
        />
      )}

      <Tooltip title={viewMode === 'grid' ? t('musicSystem.playlists.viewList') : t('musicSystem.playlists.viewGrid')}>
        <IconButton
          size="small"
          onClick={() => onViewModeChange(viewMode === 'grid' ? 'list' : 'grid')}
          sx={{ color: SONOS_BLUE, p: 0.5, flexShrink: 0 }}
        >
          {viewMode === 'grid' ? <ViewList sx={{ fontSize: 18 }} /> : <GridView sx={{ fontSize: 18 }} />}
        </IconButton>
      </Tooltip>

      <Tooltip title={t('musicSystem.playlists.new')}>
        <IconButton
          size="small"
          onClick={onAddPlaylist}
          sx={{ color: SONOS_BLUE, p: 0.5, flexShrink: 0 }}
        >
          <Add sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default PlaylistLibraryToolbar;
